import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Plus, CalendarDays, UserCheck, UserX, Ban, Crown,
  QrCode, Users, Clock, ListOrdered, CheckCircle2, Hourglass, Mail, MailX, MailWarning,
  GraduationCap, ShieldAlert, Sparkles,
} from "lucide-react";

import { api, apiErrorMessage } from "@/lib/api";
import { canWrite } from "@/lib/auth";
import { StatCard } from "@/components/StatCard";
import { StatGroup } from "@/components/StatGroup";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

function SectionLabel({ children }) {
  return <p className="kicker mb-3">{children}</p>;
}

const emptyForm = { name: "", year: new Date().getFullYear(), event_date: "" };

export default function Dashboard() {
  const readOnly = !canWrite();
  const [activeEvent, setActiveEvent] = useState(undefined); // undefined = loading, null = none
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);

  const loadEvent = async () => {
    try {
      const r = await api.get("/events/active");
      setActiveEvent(r.data && r.data.id ? r.data : null);
    } catch {
      setActiveEvent(null);
    }
  };

  const loadStats = async () => {
    try {
      const r = await api.get("/dashboard/stats");
      setStats(r.data);
    } catch {
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadEvent(); }, []);
  useEffect(() => {
    if (activeEvent === undefined) return;
    if (activeEvent) { loadStats(); } else { setLoading(false); }
  }, [activeEvent]);

  useEffect(() => {
    if (!activeEvent) return undefined;
    const refresh = () => {
      if (document.visibilityState === "visible") loadStats();
    };
    document.addEventListener("visibilitychange", refresh);
    return () => document.removeEventListener("visibilitychange", refresh);
  }, [activeEvent]);

  const createEvent = async () => {
    if (!form.name.trim() || !form.event_date) {
      toast.error("Event name and date are required");
      return;
    }
    setCreating(true);
    try {
      await api.post("/events", { name: form.name.trim(), year: Number(form.year), event_date: form.event_date });
      toast.success("Event created and set as active");
      setDialogOpen(false);
      setForm(emptyForm);
      setLoading(true);
      await loadEvent();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setCreating(false);
    }
  };

  const ml = stats?.masterlist_overview;
  const ro = stats?.registration_overview;
  const qe = stats?.qr_email_progress;
  const cp = stats?.checkin_progress;
  const pp = stats?.party_checkin_progress;
  const qp = stats?.queue_progress;
  const breakdown = stats?.category_breakdown || [];

  const createEventDialog = (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogContent className="bg-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            {activeEvent ? "Create New Event" : "Create Convocation Event"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {activeEvent && (
            <p className="text-xs text-slate-500">
              This will replace <b>{activeEvent.name}</b> as the active event. Existing registrations stay tied to the previous event.
            </p>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="event-name">Event Name</Label>
            <Input
              id="event-name" data-testid="event-name-input"
              value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Mahindra University Convocation 2026"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="event-year">Year</Label>
              <Input
                id="event-year" data-testid="event-year-input" type="number"
                value={form.year} onChange={(e) => setForm((f) => ({ ...f, year: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="event-date">Date</Label>
              <Input
                id="event-date" data-testid="event-date-input" type="date"
                value={form.event_date} onChange={(e) => setForm((f) => ({ ...f, event_date: e.target.value }))}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            data-testid="create-event-submit-btn"
            className="bg-primary hover:bg-primary/90"
            onClick={createEvent} disabled={creating}
          >
            Create Event
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title="Dashboard"
        subtitle="Live overview of registrations, check-ins and stage progress for the active convocation."
        actions={
          <div className="flex items-center gap-3">
            {activeEvent && (
              <Badge variant="outline" className="flex items-center gap-1.5 py-1.5 px-3" data-testid="active-event-badge">
                <CalendarDays size={12} />
                {activeEvent.name} · {activeEvent.event_date}
              </Badge>
            )}
            {!readOnly && (
              <Button
                data-testid="open-create-event-btn"
                variant={activeEvent ? "outline" : "default"}
                className={activeEvent ? "" : "bg-primary hover:bg-primary/90"}
                onClick={() => setDialogOpen(true)}
              >
                <Plus size={14} className="mr-1.5" /> {activeEvent ? "New Event" : "Create Event"}
              </Button>
            )}
          </div>
        }
      />
      {createEventDialog}

      {loading ? (
        <p className="text-slate-500">Loading…</p>
      ) : !activeEvent ? (
        <div className="max-w-xl bg-white border border-dashed border-border rounded-xl p-10 text-center">
          <p className="kicker">No active event</p>
          <h2 className="font-serif text-2xl mt-3">
            {readOnly ? "No active convocation event" : "Create a convocation event to get started"}
          </h2>
          <p className="text-slate-500 mt-3">
            {readOnly
              ? "Dashboard stats will appear here once an admin activates a convocation event."
              : "Once an event is active, you can upload registrations, configure the stage sequence, and run check-ins against it."}
          </p>
          {!readOnly && (
            <Button className="mt-6 bg-primary hover:bg-primary/90" onClick={() => setDialogOpen(true)} data-testid="empty-create-event-btn">
              <Plus size={16} className="mr-2" /> Create Event
            </Button>
          )}
        </div>
      ) : (
        <>
          <section className="mb-6">
            <SectionLabel>Masterlist</SectionLabel>
            <StatGroup>
              <StatCard testId="stat-ml-total" label="Total Students" value={ml?.total_students ?? "—"} icon={GraduationCap} color="slate" />
              <StatCard testId="stat-ml-attending" label="Attending" value={ml?.attending ?? "—"} icon={UserCheck} color="green" />
              <StatCard testId="stat-ml-not-attending" label="Not Attending" value={ml?.not_attending ?? "—"} icon={UserX} color="amber" />
              <StatCard testId="stat-ml-blocked" label="Blocked (Fee Not Cleared)" value={ml?.blocked ?? "—"} icon={ShieldAlert} color="red" />
            </StatGroup>
          </section>

          <section className="mb-6">
            <SectionLabel>Registration Overview</SectionLabel>
            <StatGroup>
              <StatCard testId="stat-registered" label="Registered (Attending)" value={ro?.registered_attending ?? "—"} icon={UserCheck} color="green" />
              <StatCard testId="stat-not-attending" label="Not Attending" value={ro?.not_attending ?? "—"} icon={UserX} color="slate" />
              <StatCard testId="stat-excluded" label="Excluded from Sequence" value={ro?.excluded_from_sequence ?? "—"} icon={Ban} color="red" />
              <StatCard testId="stat-priority" label="Priority / Gold Medalist" value={ro?.priority_count ?? "—"} icon={Crown} color="amber" />
            </StatGroup>
          </section>

          <section className="mb-6">
            <SectionLabel>QR Email Progress</SectionLabel>
            <StatGroup>
              <StatCard
                testId="stat-qr-emailed"
                label="QR Emails Sent"
                value={qe ? `${qe.sent} / ${qe.total_attending}` : "—"}
                icon={Mail} color="green"
              />
              <StatCard
                testId="stat-qr-not-sent"
                label="Not Sent Yet"
                value={qe?.not_sent ?? "—"}
                icon={MailWarning} color="amber"
              />
              <StatCard
                testId="stat-qr-failed"
                label="Send Failed"
                value={qe?.failed ?? "—"}
                icon={MailX} color="red"
              />
              <StatCard
                testId="stat-qr-skipped"
                label="Skipped (No Email)"
                value={qe?.skipped ?? "—"}
                icon={Ban} color="slate"
              />
            </StatGroup>
          </section>

          <section className="mb-6">
            <SectionLabel>Check-in Progress</SectionLabel>
            <StatGroup>
              <StatCard
                testId="stat-student-checkins"
                label="Student Check-ins"
                value={cp ? `${cp.student_checkins} / ${cp.student_total}` : "—"}
                icon={QrCode} color="blue"
              />
              <StatCard
                testId="stat-guest-checkins"
                label="Guest Check-ins"
                value={cp ? `${cp.guest_checkins} / ${cp.guest_total}` : "—"}
                icon={Users} color="blue"
              />
              <StatCard testId="stat-pending" label="Pending Student Check-ins" value={cp?.pending_student_checkins ?? "—"} icon={Clock} color="amber" />
            </StatGroup>
          </section>

          <section className="mb-6">
            <SectionLabel>Party Check-in Progress</SectionLabel>
            <StatGroup>
              <StatCard
                testId="stat-party-student-checkins"
                label="Student Party Check-ins"
                value={pp ? `${pp.student_checkins} / ${pp.student_total}` : "—"}
                icon={Sparkles} color="primary"
              />
              <StatCard
                testId="stat-party-guest-checkins"
                label="Guest Party Check-ins"
                value={pp ? `${pp.guest_checkins} / ${pp.guest_total}` : "—"}
                icon={Users} color="primary"
              />
              <StatCard
                testId="stat-party-pending-students"
                label="Pending Student Party Check-ins"
                value={pp?.pending_student_checkins ?? "—"}
                icon={Clock} color="amber"
              />
              <StatCard
                testId="stat-party-pending-guests"
                label="Pending Guest Party Check-ins"
                value={pp?.pending_guest_checkins ?? "—"}
                icon={Clock} color="amber"
              />
            </StatGroup>
          </section>

          <section className="mb-10">
            <SectionLabel>Stage / Queue Progress</SectionLabel>
            <StatGroup>
              <StatCard testId="stat-priority-waiting" label="Priority Waiting" value={qp?.priority?.waiting ?? "—"} icon={Crown} color="amber" />
              <StatCard testId="stat-normal-waiting" label="Normal Waiting" value={qp?.normal?.waiting ?? "—"} icon={ListOrdered} color="slate" />
              <StatCard testId="stat-late-waiting" label="Late Waiting" value={qp?.late?.waiting ?? "—"} icon={Hourglass} color="red" />
              <StatCard testId="stat-total-announced" label="Total Announced" value={qp?.total_announced ?? "—"} icon={CheckCircle2} color="green" accent />
              <StatCard testId="stat-total-remaining" label="Total Remaining" value={qp?.total_remaining ?? "—"} icon={Hourglass} color="slate" />
            </StatGroup>
          </section>

          <section className="bg-white border border-border rounded-xl">
            <div className="px-6 py-4 border-b border-border">
              <p className="kicker">By Category</p>
              <h2 className="font-serif text-2xl mt-1">Category Breakdown</h2>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Entity</TableHead>
                    <TableHead>Program</TableHead>
                    <TableHead>Specialization</TableHead>
                    <TableHead className="text-center">Students</TableHead>
                    <TableHead className="text-center">Registered</TableHead>
                    <TableHead className="text-center">Checked In</TableHead>
                    <TableHead className="text-center">Announced</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {breakdown.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-500">No data yet.</TableCell></TableRow>
                  )}
                  {breakdown.map((b, i) => (
                    <TableRow key={i} data-testid={`category-row-${i}`}>
                      <TableCell>{b.entity}</TableCell>
                      <TableCell>{b.program}</TableCell>
                      <TableCell>{b.specialization}</TableCell>
                      <TableCell className="text-center">{b.students ?? 0}</TableCell>
                      <TableCell className="text-center">{b.registered}</TableCell>
                      <TableCell className="text-center">{b.checked_in}</TableCell>
                      <TableCell className="text-center">{b.announced}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
